import React, { useEffect, useRef, useState } from "react";
import { ITkCorporateSearchModalProps } from "../../../type/componentsType/TkCorporateSearchModa";
import TkModalWithTitle from "../../atoms/TkModalWithTitle";
import TkDataTable from "../TkDataTable";
import { ITkDataTableHeadePorps } from "../../../type/componentsType/TkDataTable";
import TkRadio from "../../atoms/TkRadio";
import { Alert, Box, Typography } from "@mui/material";
import { Prefectures } from "../../../assets/data/Constants";
import "./_style.scss";
import TkCheckbox from "../../atoms/TkCheckbox";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import { TkGetCode, TkGetCodeList } from "../../../utils/TkGetCode";
import { TkSearchFormListItem } from "../../../type/componentsType/TkSearchForm";
import TkSearchForm from "../TkSearchForm";
import TkLoadingIcon from "../../../components/atoms/TkLoadingIcon";

/**
 * TkCorporateSearchModalコンポーネント
 * @component
 * @param {ITkCorporateSearchModalProps} props - コンポーネントのプロパティ
 * @param {boolean} props.open - 表示／非表示。
 * @param {boolean} props.useCheck - ラジオボタン選択形式／チェックボックス選択形式。
 * @returns {React.FC} TkCorporateSearchModalコンポーネント
 */
const TkCorporateSearchModal: React.FC<ITkCorporateSearchModalProps> = (
  props
) => {
  const {
    open,
    listData,
    useCheck = false,
    onChangeSearchData,
    onSave,
    onClose,
  } = props;

  const [bodyList, setBodyList] = useState<any>();
  const [selectItem, setSelectItem] = useState<any>();
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const pageValueRef = useRef(0);
  const pageValue = pageValueRef.current;
  const setPageValue = (value: number) => {
    pageValueRef.current = value;
  };

  const headList: ITkDataTableHeadePorps[] = [
    {
      headTitle: "選択",
      dontNeedSort: true,
    },
    {
      headTitle: "法人名称",
    },
    {
      headTitle: "所在地",
    },
    {
      headTitle: "電話番号",
    },
    {
      headTitle: "メールアドレス",
    },
  ];

  const searchList: TkSearchFormListItem[] = [
    {
      label: "契約者番号",
      keyword: "contractorNo",
      type: "label",
      onlyNumber: true,
      inputProps: {
        maxLength: 6,
      },
    },
    {
      label: "法人名称",
      keyword: "corpName",
      type: "label",
      inputProps: {
        maxLength: 85,
      },
    },
    {
      label: "都道府県",
      keyword: "prefectureCd",
      type: "selectFrom",
      selectPropsList: TkGetCodeList(Prefectures),
    },
  ];

  const schema = {
    contractorNo: {
      pattern: {
        value: /^(\d{6})?$/,
        message: TkPropertyMessageGet("TKMSG_SCR0077E", "6"),
      },
    },
    corpName: {
      pattern: {
        // eslint-disable-next-line no-control-regex
        value: /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F -~｡-ﾟ]+$/,
        message: TkPropertyMessageGet("TKMSG_SCR0061E"),
      },
    },
  };

  const handelItemSelect = (e: any) => {
    if (useCheck) {
      const res = e;
      setSelectItem((prevSelectItem: any) => {
        if (prevSelectItem) {
          return prevSelectItem.includes(res)
            ? prevSelectItem.filter((item: any) => item !== res)
            : [...prevSelectItem, res];
        } else {
          return [res];
        }
      });
      return;
    }

    setSelectItem(e);
  };

  const handleSearch = (data: any) => {
    if (Object.values(data).every((item) => !item)) {
      setError(true);
      setSuccess(true);
      return;
    }

    setError(false);
    setSuccess(false);
    onChangeSearchData && onChangeSearchData(data);
  };

  const handleOnSave = () => {
    setError(false);
    setSuccess(false);
    onSave && onSave(selectItem);
  };

  const handleOnClose = () => {
    setError(false);
    onClose && onClose();
  };

  const handlePageChange = (_: any, page: any) => {
    setPageValue(page - 1);
  };

  useEffect(() => {
    const renderList = listData.map((item) => {
      const {
        contractorNo,
        corpName,
        prefectureCd,
        addressCity,
        addressOthers,
        tel,
        contactEmail,
      } = item;
      const list = [
        {
          children: useCheck ? (
            <TkCheckbox
              checked={selectItem && selectItem.includes(item) ? true : false}
              onClick={() => handelItemSelect(item)}
            />
          ) : (
            <TkRadio
              checked={
                selectItem && selectItem?.contractorNo === contractorNo
                  ? true
                  : false
              }
              onClick={() => handelItemSelect(item)}
            />
          ),
          width: 40,
        },
        {
          children: (
            <Typography>
              {contractorNo}:{corpName}
            </Typography>
          ),
          forSort: contractorNo,
          width: 250,
        },
        {
          children: (
            <Typography>
              {TkGetCode(Prefectures, prefectureCd)}
              {addressCity}
              {addressOthers}
            </Typography>
          ),
          forSort: `${prefectureCd}${addressCity}${addressOthers}`,
        },
        {
          children: <Typography>{tel}</Typography>,
          forSort: tel,
          width: 130,
        },
        {
          children: <Typography>{contactEmail}</Typography>,
          forSort: contactEmail,
          width: 160,
        },
      ];

      return list;
    });

    setBodyList([...renderList]);
    setSuccess(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData, selectItem]);

  return (
    <TkModalWithTitle
      title="法人検索"
      saveData={selectItem}
      clearSaveData={() => setSelectItem(null)}
      open={open}
      saveButtonDisabled={!selectItem || (useCheck && !selectItem?.length)}
      onSave={handleOnSave}
      onClose={handleOnClose}
      className={"TkCorporateSearchModalScroll"}
    >
      <>
        {error && (
          <Alert severity="error">
            {TkPropertyMessageGet("TKMSG_SVR0067E")}
          </Alert>
        )}
        <Box className={"TkModalWithTitleSec"}>
          <TkSearchForm
            listData={searchList}
            ruleSchema={schema}
            onSearch={handleSearch}
          />
        </Box>
        {success ? (
          <TkDataTable
            useScroll
            bodyList={bodyList}
            headList={headList}
            pagin={true}
            dontResetPage
            onPageChange={handlePageChange}
            setPageValue={pageValue}
          />
        ) : (
          <TkLoadingIcon />
        )}
      </>
    </TkModalWithTitle>
  );
};

export default TkCorporateSearchModal;
